import React from 'react';

import { getSplashTone } from './BootSplash.utils';

/**
 * The spinner a player sees while the portal holds its UI back — on a
 * credential landing, from the document painting until the login settles, the
 * stretch that used to be a blank, publisher-colored page.
 *
 * It costs nothing to show: rendered on the server as part of the document, so
 * it paints the moment the document does — no request, no JavaScript, under
 * 1 KB of inline markup. It carries no text and no image, so paint timing never
 * counts it as content: FCP and LCP still measure the portal, not the splash.
 * Its caller decides how long it stays; the splash has no lifetime of its own,
 * so it survives hydration for as long as the wait does.
 *
 * If the JavaScript never arrives, the CSS animation fades the spinner out on
 * its own after {@link SPLASH_MAX_VISIBLE_S}: a page that cannot boot must not
 * spin forever, and the chunk-recovery flow owns that document anyway.
 *
 * The look is the store's AcCircularLoader (48px ring, 0.8s spin), in the tone
 * that contrasts with the publisher's background; a background it cannot read
 * gets the light ring with a thin drop shadow, which reads on either.
 */

/** How long the splash may spin before CSS hides it on a page whose JS never came. */
const SPLASH_MAX_VISIBLE_S = 12;

const SPLASH_CSS = `
#ac-boot-splash {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 50;
  animation: ac-boot-splash-hide 0.4s ease ${SPLASH_MAX_VISIBLE_S}s forwards;
}
#ac-boot-splash .ac-boot-splash__spinner {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.25);
  border-top-color: #ffffff;
  filter: drop-shadow(0 0 1px rgba(0, 0, 0, 0.45));
  animation: ac-boot-splash-spin 0.8s linear infinite;
}
#ac-boot-splash[data-tone='light'] .ac-boot-splash__spinner {
  filter: none;
}
#ac-boot-splash[data-tone='dark'] .ac-boot-splash__spinner {
  border-color: rgba(0, 0, 0, 0.15);
  border-top-color: rgba(0, 0, 0, 0.7);
  filter: none;
}
@media (prefers-reduced-motion: reduce) {
  #ac-boot-splash .ac-boot-splash__spinner {
    animation-duration: 2.4s;
  }
}
@keyframes ac-boot-splash-spin {
  to { transform: rotate(360deg); }
}
@keyframes ac-boot-splash-hide {
  to { opacity: 0; visibility: hidden; }
}
`;

interface IBootSplashProps {
  /** The color the document paints behind the splash. */
  backgroundColor?: string;
}

export const BootSplash = ({ backgroundColor }: IBootSplashProps): React.ReactElement => (
  <div
    id="ac-boot-splash"
    data-testid="boot-splash"
    data-tone={getSplashTone(backgroundColor)}
    aria-hidden>
    <style>{SPLASH_CSS}</style>
    <span className="ac-boot-splash__spinner" />
  </div>
);
